import React from 'react';
import { Alert, Button, Checkbox, Form, Input, Modal, Select, Table, Tabs, Tag } from 'antd';
import GroupPermissionAuthModal from './GroupPermissionAuthModal';
import UserMemberScopeModal from './UserMemberScopeModal';
import { getSystemRoleMeta, normalizeSystemRole } from '../../utils/systemRoles';

/** 构建权限管理标签页，通过上下文接收原有状态和操作函数。 */
export function buildPermissionsTab(context) {
  const { shouldShowPage, groupSearch, setGroupSearch, handleAddGroup, groups, selectedGroupKeys, setSelectedGroupKeys, handleMoveGroup, handleEditGroup, handleDeleteGroup, fetchGroupAuthPerms, groupModalVisible, setGroupModalVisible, groupForm, editingGroup, handleSaveGroup, groupAuthId, setGroupAuthId, handleSaveGroupAuth, permissions, groupAuthPerms, setGroupAuthPerms, groupAuthMsg, permSearch, setPermSearch, editingPerm, setEditingPerm, permForm, setPermModalVisible, permModalVisible, handleSavePerm, handleEditPerm, handleDeletePerm, users, fetchUserAuthGroups, userAuthId, userAuthGroups, setUserAuthGroups, handleSaveUserAuth, userAuthMsg, userPerms, fetchPermissionPreview, permissionPreview, permissionPreviewUserId, setPermissionPreviewUserId, memberScopeModalVisible, memberScopeUserId, memberScopeUserName, openMemberScopeModal, closeMemberScopeModal, apiRequest } = context;
  const authGroupName = groups?.find((g) => g.id === groupAuthId)?.name || '';
  const tabs = [];
    // 权限管理（分组 / 权限点 / 用户授权 / 权限预览 可按细权限独立显示）
    if (shouldShowPage('分组管理') || shouldShowPage('权限管理') || shouldShowPage('用户授权')) {
      const permTabs = [];
      
      if (shouldShowPage('分组管理')) {
        const filteredGroups = groups.filter(g => !groupSearch || g.name.includes(groupSearch));
        const sortingLocked = !!groupSearch;
        permTabs.push({
          key: 'groups',
          label: '分组管理',
          children: (
            <div>
              <div style={{ marginBottom: 16 }}>
                <Input.Search placeholder="搜索分组" value={groupSearch} onChange={e => setGroupSearch(e.target.value)} style={{ width: 200, marginRight: 8 }} />
                <Button type="primary" onClick={handleAddGroup}>添加分组</Button>
              </div>
              {sortingLocked && (
                <div style={{ color: '#b45309', marginBottom: 8, fontSize: 12 }}>
                  搜索过滤中已锁定排序，清空搜索后再调整上下顺序。
                </div>
              )}
              <Table 
                dataSource={filteredGroups}
                rowSelection={{ selectedRowKeys: selectedGroupKeys, onChange: setSelectedGroupKeys }}
                rowKey="id"
                columns={[
                  { 
                    title: '排序', 
                    key: 'sort', 
                    width: 120,
                    render: (_, record, index) => (
                      <span style={{ display: 'flex', gap: '4px' }}>
                        <Button 
                          size="small" 
                          type="text"
                          disabled={sortingLocked || index === 0}
                          onClick={() => handleMoveGroup(record.id, 'up')}
                          style={{ padding: '2px 4px', minWidth: 'auto' }}
                        >
                          ↑
                        </Button>
                        <Button 
                          size="small" 
                          type="text"
                          disabled={sortingLocked || index === filteredGroups.length - 1}
                          onClick={() => handleMoveGroup(record.id, 'down')}
                          style={{ padding: '2px 4px', minWidth: 'auto' }}
                        >
                          ↓
                        </Button>
                      </span>
                    )
                  },
                  { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
                  { title: '分组名', dataIndex: 'name', key: 'name' },
                  { title: '描述', dataIndex: 'description', key: 'description' },
                  { title: '创建时间', dataIndex: 'created_at', key: 'created_at', width: 180, render: (text) => {
                    if (!text) return '-';
                    const d = new Date(text);
                    return isNaN(d.getTime()) ? '-' : d.toLocaleString();
                  } },
                  { title: '操作', key: 'action', width: 200, render: (_, record) => (
                    <span>
                      <Button type="link" onClick={() => handleEditGroup(record)}>编辑</Button>
                      <Button type="link" onClick={() => handleDeleteGroup(record.id)}>删除</Button>
                      <Button type="link" onClick={() => fetchGroupAuthPerms(record.id)}>授权</Button>
                    </span>
                  ) }
                ]} 
              />
              <Modal
                open={groupModalVisible}
                onCancel={() => setGroupModalVisible(false)}
                onOk={() => groupForm.submit()}
                title={editingGroup ? '编辑分组' : '添加分组'}
              >
                <Form form={groupForm} onFinish={handleSaveGroup}>
                  <Form.Item name="name" label="分组名" rules={[{ required: true, message: '请输入分组名' }]}><Input /></Form.Item>
                  <Form.Item name="description" label="描述"><Input.TextArea /></Form.Item>
                </Form>
              </Modal>
              <GroupPermissionAuthModal
                open={!!groupAuthId}
                groupName={authGroupName}
                permissions={permissions}
                selectedPermissionIds={groupAuthPerms}
                savingMsg={groupAuthMsg}
                onCancel={() => setGroupAuthId(null)}
                onOk={handleSaveGroupAuth}
                onChangeSelectedIds={(ids) => setGroupAuthPerms(ids.map((id) => Number(id)))}
              />
            </div>
          )
        });
      }
      
      if (shouldShowPage('权限管理')) {
        permTabs.push({
          key: 'permissions',
          label: '权限点管理',
          children: (
            <div>
              <div style={{ marginBottom: 16 }}>
                <Input.Search placeholder="搜索权限点" value={permSearch} onChange={e => setPermSearch(e.target.value)} style={{ width: 200, marginRight: 8 }} />
                <Button type="primary" onClick={() => { setEditingPerm(null); permForm.resetFields(); setPermModalVisible(true); }}>添加权限点</Button>
              </div>
              <Table 
                dataSource={permissions.filter(p => !permSearch || p.name.includes(permSearch))}
                rowKey="id"
                columns={[
                  { title: 'ID', dataIndex: 'id', key: 'id' },
                  { title: '权限点', dataIndex: 'name', key: 'name' },
                  { title: '描述', dataIndex: 'description', key: 'description' },
                  { title: '访问控制', dataIndex: 'restrict_access', key: 'restrict_access', render: (restrict_access) => (
                    <Tag color={restrict_access ? 'red' : 'green'}>
                      {restrict_access ? '🔒 仅限有权限用户' : '🌐 所有用户可见'}
                    </Tag>
                  ) },
                  { title: '创建时间', dataIndex: 'created_at', key: 'created_at', render: (text) => {
                    if (!text) return '-';
                    const d = new Date(text);
                    return isNaN(d.getTime()) ? '-' : d.toLocaleString();
                  } },
                  { title: '操作', key: 'action', render: (_, record) => (
                    <span>
                      <Button type="link" onClick={() => handleEditPerm(record)}>编辑</Button>
                      <Button type="link" danger onClick={() => handleDeletePerm(record.id)}>删除</Button>
                    </span>
                  ) }
                ]} 
              />
              <Modal
                open={permModalVisible}
                onCancel={() => setPermModalVisible(false)}
                onOk={() => permForm.submit()}
                title={editingPerm ? '编辑权限点' : '添加权限点'}
              >
                <Form form={permForm} onFinish={handleSavePerm} initialValues={editingPerm || {}}>
                  <Form.Item name="name" label="权限点" rules={[{ required: true, message: '请输入权限点名称' }]}><Input /></Form.Item>
                  <Form.Item name="description" label="描述"><Input /></Form.Item>
                  <Form.Item name="restrict_access" label="访问控制" valuePropName="checked">
                    <Checkbox>仅对有权限的用户可见</Checkbox>
                  </Form.Item>
                </Form>
              </Modal>
            </div>
          )
        });
      }

      if (shouldShowPage('用户授权')) {
        permTabs.push({
          key: 'user_auth',
          label: '用户授权',
          children: (
            <div>
              <div style={{ marginBottom: 16 }}>
                <Select 
                  placeholder="选择用户" 
                  style={{ width: 300, marginRight: 8 }}
                  value={userAuthId}
                  onChange={fetchUserAuthGroups}
                  showSearch
                  filterOption={(input, option) =>
                    String(option?.children || option?.label || '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {(users || []).map(user => (
                    <Select.Option key={user.id} value={user.id}>
                      {user.username} ({user.email})
                    </Select.Option>
                  ))}
                </Select>
              </div>
              {userAuthId && (
                <div>
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ marginBottom: 8 }}>选择要授权的分组：</div>
                    <Checkbox.Group
                      options={groups.map(g => ({ label: g.name, value: g.id }))}
                      value={userAuthGroups}
                      onChange={vals => setUserAuthGroups(vals.map(v => Number(v)))}
                      style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}
                    />
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <Button type="primary" onClick={handleSaveUserAuth}>保存用户授权</Button>
                    <Button 
                      style={{ marginLeft: 8 }} 
                      onClick={() => {
                        const uid = Number(userAuthId);
                        const user = (users || []).find(u => Number(u.id) === uid);
                        openMemberScopeModal(uid, user?.username || '');
                      }}
                    >
                      配置队员可见范围
                    </Button>
                  </div>
                  {userAuthMsg && <div style={{ color: userAuthMsg.includes('成功') ? 'green' : 'red', marginTop: 16 }}>{userAuthMsg}</div>}
                  
                  {userPerms.length > 0 && (
                    <div style={{ marginTop: 16 }}>
                      <h4>用户当前权限：</h4>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {userPerms.map(perm => (
                          <Tag key={perm.id} color="blue">{perm.name}</Tag>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        });
      }

      if (shouldShowPage('用户授权') || shouldShowPage('用户管理') || shouldShowPage('权限管理')) {
        permTabs.push({
          key: 'permission_preview',
          label: '权限预览',
          children: (
            <div>
              <div style={{ marginBottom: 16 }}>
                <Select
                  placeholder="选择用户"
                  style={{ width: 300, marginRight: 8 }}
                  value={permissionPreviewUserId}
                  onChange={(userId) => setPermissionPreviewUserId?.(userId)}
                  showSearch
                  filterOption={(input, option) =>
                    String(option?.children || '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {(users || []).map(user => (
                    <Select.Option key={user.id} value={user.id}>
                      {user.username} ({user.email})
                    </Select.Option>
                  ))}
                </Select>
                <Button
                  type="primary"
                  disabled={!permissionPreviewUserId}
                  onClick={() => fetchPermissionPreview?.(permissionPreviewUserId)}
                >
                  查看实际权限
                </Button>
              </div>

              {permissionPreview?.modelNote && (
                <Alert type="info" message={permissionPreview.modelNote} style={{ marginBottom: 16 }} showIcon />
              )}

              {permissionPreview?.user && (
                <div style={{ marginBottom: 16 }}>
                  <strong>用户：</strong>
                  {permissionPreview.user.username}
                  {permissionPreview.user.real_name ? ` (${permissionPreview.user.real_name})` : ''}
                  {' · '}
                  <Tag color={getSystemRoleMeta(permissionPreview.user.role).color}>
                    {normalizeSystemRole(permissionPreview.user.role)}
                  </Tag>
                </div>
              )}

              {permissionPreview?.groups?.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <strong>所属分组：</strong>
                  {permissionPreview.groups.map(g => (
                    <Tag key={g.id}>{g.name}</Tag>
                  ))}
                </div>
              )}

              {permissionPreview?.permissions?.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <strong>权限点：</strong>
                  {permissionPreview.permissions.map(name => (
                    <Tag key={name} color="blue">{name}</Tag>
                  ))}
                </div>
              )}

              {permissionPreview?.pageAccess?.length > 0 && (
                <Table
                  dataSource={permissionPreview.pageAccess}
                  rowKey="name"
                  pagination={{ pageSize: 20 }}
                  size="small"
                  columns={[
                    { title: '页面/权限点', dataIndex: 'name', key: 'name' },
                    {
                      title: 'hasPermission',
                      dataIndex: 'hasPermission',
                      key: 'hasPermission',
                      width: 120,
                      render: (v) => (v ? '是' : '否')
                    },
                    {
                      title: 'shouldShow',
                      dataIndex: 'shouldShow',
                      key: 'shouldShow',
                      width: 120,
                      render: (v) => (v ? '是' : '否')
                    },
                    {
                      title: 'canAccess',
                      dataIndex: 'canAccess',
                      key: 'canAccess',
                      width: 120,
                      render: (v) => (v ? '是' : '否')
                    }
                  ]}
                />
              )}
            </div>
          )
        });
      }
      
      if (permTabs.length > 0) {
        tabs.push({
          key: 'permissions',
          label: '权限管理',
          children: (
            <>
              <Tabs defaultActiveKey={permTabs[0].key} items={permTabs} />
              <UserMemberScopeModal
                visible={memberScopeModalVisible}
                userId={memberScopeUserId}
                userName={memberScopeUserName}
                onClose={closeMemberScopeModal}
                apiRequest={apiRequest}
              />
            </>
          )
        });
      }
    }

  return tabs[0] || null;
}
